import { useState, FormEvent, useEffect, useRef } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useTurnstile } from '../../hooks/useTurnstile'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

// Use Cloudflare test keys for localhost development
// Test key always passes validation - see: https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA' // Always passes
const TURNSTILE_SITE_KEY = isLocalhost 
    ? TURNSTILE_TEST_SITE_KEY 
    : (import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACGx6un5T9N0O-rB')

export function WaitlistForm() {
    const [email, setEmail] = useState('')
    const [helpWith, setHelpWith] = useState('')
    const [status, setStatus] = useState<FormStatus>('idle')
    const [errorMessage, setErrorMessage] = useState('')
    const [token, setToken] = useState<string | null>(null)
    const widgetId = useRef<string | null>(null)
    const turnstileContainerRef = useRef<HTMLDivElement>(null)

    // Load Turnstile script
    const isTurnstileReady = useTurnstile()

    // Render widget when script is ready
    useEffect(() => {
        if (isTurnstileReady && turnstileContainerRef.current && !widgetId.current) {
            try {
                // Clear container just in case
                turnstileContainerRef.current.innerHTML = ''

                widgetId.current = window.turnstile.render(turnstileContainerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    theme: 'auto',
                    size: 'flexible',
                    callback: (token) => {
                        setToken(token)
                        if (status === 'error') {
                            setStatus('idle')
                            setErrorMessage('')
                        }
                    },
                    'error-callback': (errorCode) => {
                        console.error('Turnstile error:', errorCode)
                        setErrorMessage('Security check failed. Please try again.')
                        setToken(null)
                    },
                    'expired-callback': () => {
                        setToken(null)
                        setErrorMessage('Security check expired. Please verify again.')
                    }
                })
            } catch (e) {
                console.error('Turnstile render error:', e)
            }
        }
    }, [isTurnstileReady, status]) // Re-render if status changes back to idle (e.g. after error)? No, render once.

    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()

        if (!validateEmail(email)) {
            setStatus('error')
            setErrorMessage('Please enter a valid email address')
            return
        }

        if (!token) {
            setStatus('error')
            setErrorMessage('Please complete the security check')
            return
        }

        setStatus('loading')
        setErrorMessage('')

        try {
            // Use local Edge Function endpoint or production URL
            // If VITE_WAITLIST_API_ENDPOINT is not set, defaulting to a relative path for proxying or direct absolute URL if known
            // Usually local dev: http://localhost:54321/functions/v1/waitlist-signup
            // Production: https://[project-ref].supabase.co/functions/v1/waitlist-signup

            const functionUrl = import.meta.env.VITE_WAITLIST_API_ENDPOINT || 'https://sefsavkgllogzmogbwcl.supabase.co/functions/v1/waitlist-signup'
            const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
            // Note: In real prod, this env var needs to be set. For now relying on it or fallback.

            // Actually, better to use the supabase client if we had it, but fetch is fine.
            // Let's use fetch for simplicity as we don't need auth here (it's public).

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': anonKey,
                    'Authorization': `Bearer ${anonKey}`
                },
                body: JSON.stringify({
                    email,
                    helpWith,
                    turnstileToken: token
                }),
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || 'Submission failed')
            }

            setStatus('success')
            setEmail('')
            setHelpWith('')
            setToken(null)
            if (widgetId.current) {
                window.turnstile.remove(widgetId.current)
                widgetId.current = null
            }
        } catch (error) {
            const err = error as Error
            setStatus('error')
            setErrorMessage(err.message || 'Something went wrong. Please try again.')
            if (widgetId.current) {
                window.turnstile.reset(widgetId.current)
                setToken(null)
            }
        }
    }

    if (status === 'success') {
        return (
            <div className="bg-paper-alt rounded-2xl p-8 border border-paper-tertiary shadow-card animate-fade-in">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-success-bg flex items-center justify-center">
                        <Check className="w-8 h-8 text-success" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-xl font-semibold text-ink mb-1">You're in.</h3>
                        <p className="text-ink-tertiary">
                            We'll email you when the beta is ready.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setStatus('idle')
                            // Widget will re-render due to status change if we handle it correctly?
                            // Actually useEffect dependency on status isn't ideal for re-rendering widget.
                            // But since we removed it on success, we need to handle re-mounting.
                            // The component re-renders, useEffect runs, widgetId is null, so it renders again.
                        }}
                        className="text-sm text-ink-tertiary hover:text-ink underline underline-offset-4 transition-colors"
                    >
                        Register another email
                    </button>
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
            {/* Email Input */}
            <div className="relative">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value)
                        if (status === 'error') setStatus('idle')
                    }}
                    placeholder="Enter your email"
                    className="w-full h-14 px-5 bg-input-bg border border-input-border rounded-xl text-ink placeholder:text-ink-disabled focus:border-input-border-focus focus:ring-2 focus:ring-cta/10 transition-all"
                    disabled={status === 'loading'}
                />
            </div>

            {/* Optional: Human, concrete question */}
            <div className="relative">
                <input
                    type="text"
                    value={helpWith}
                    onChange={(e) => setHelpWith(e.target.value)}
                    placeholder="What's the hardest part of getting dressed? (optional)"
                    className="w-full h-14 px-5 bg-input-bg border border-input-border rounded-xl text-ink placeholder:text-ink-disabled focus:border-input-border-focus focus:ring-2 focus:ring-cta/10 transition-all"
                    disabled={status === 'loading'}
                />
                <p className="mt-1.5 text-xs text-ink-muted px-1">
                    e.g. work days, dinners, travel, building confidence, simplifying my closet...
                </p>
            </div>

            {/* Turnstile Widget Container */}
            <div className="min-h-[65px] flex justify-center">
                <div ref={turnstileContainerRef} className="w-full" />
            </div>

            {/* Error Message Area */}
            {status === 'error' && (
                <p className="text-sm text-center text-error animate-fade-in">
                    {errorMessage}
                </p>
            )}

            {/* Submit Button */}
            <button
                type="submit"
                disabled={status === 'loading' || !token}
                className="w-full h-14 flex items-center justify-center gap-2 bg-cta text-white font-semibold rounded-xl hover:bg-cta-hover disabled:bg-cta-disabled disabled:cursor-not-allowed transition-colors shadow-lg hover:shadow-xl"
            >
                {status === 'loading' ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Joining...
                    </>
                ) : (
                    'Join the waitlist'
                )}
            </button>

            <p className="text-xs text-ink-tertiary text-center space-y-0.5">
                <span className="block">No spam. Unsubscribe anytime.</span>
                <span className="block text-ink-muted">We're opening access gradually.</span>
            </p>
        </form>
    )
}
